import { useMatch, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { EditorSearch, editorSearchSchema, PdfSearch, pdfSearchSchema, DocumentSearch, documentSearchSchema } from './search';

type RouteParamName = 'documentId' | 'sectionId' | 'refId' | 'templateId' | 'documentRevisionId' | 'id';

export const useRouteParams = () => useParams({ strict: false }) as Partial<Record<RouteParamName, string>>;

export const useRequiredParams = <K extends RouteParamName>(...names: K[]): Record<K, string> => {
  const params = useRouteParams();
  const missing = names.filter((name) => !params[name]);
  if (missing.length) throw new Error(`Route params required here but not in the URL: ${missing.join(', ')}`);
  return Object.fromEntries(names.map((name) => [name, params[name]])) as Record<K, string>;
};

export const useDocumentParams = () => useRequiredParams('documentId');

const SECTION_ROUTE_PATH = '/documents/$documentId/sections/$sectionId';
const SBS_ROUTE_PATH = '/sbs/documents/$documentId/sections/$sectionId';
const SBS_ROUTE_ID = '/app/sbs/documents/$documentId/sections/$sectionId';
const TAGGING_ROUTE_ID = '/app/documents/$documentId/tagging';
const TAGGING_ROUTE_PATH = '/documents/$documentId/tagging';
const PDF_ROUTE_ID = '/app/documents/$documentId/pdf/$documentRevisionId';

export const useEditorNavigate = () => {
  const isSideBySide = !!useMatch({ from: SBS_ROUTE_ID, shouldThrow: false });
  return useNavigate({ from: isSideBySide ? SBS_ROUTE_PATH : SECTION_ROUTE_PATH });
};

export const useEditorSearch = (): EditorSearch => editorSearchSchema.parse(useSearch({ strict: false }));
export const useIsViewingVersion = (): boolean => !!useEditorSearch().revision;
export const usePdfSearch = (): PdfSearch => pdfSearchSchema.parse(useSearch({ strict: false }));
export const useDocumentSearch = (): DocumentSearch => documentSearchSchema.parse(useSearch({ strict: false }));

export const useTaggingSearch = () => useSearch({ from: TAGGING_ROUTE_ID });
export const useDocumentNavigate = () => useNavigate({ from: '/documents/$documentId' });
export const useTaggingNavigate = () => useNavigate({ from: TAGGING_ROUTE_PATH });
export const useIsExportRender = () => !!useMatch({ from: PDF_ROUTE_ID, shouldThrow: false });
